'use client';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  useAppDispatch,
  useAppSelector,
} from '@/lib/client-hook/use-app-dispatch';
import { setTossPay } from '@/slice/tossPay';
import {
  loadTossPayments,
  TossPaymentsWidgets,
  WidgetPaymentMethodWidget,
} from '@tosspayments/tosspayments-sdk';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import '@/app/input.css';
import CloseIcon from '@/public/images/ic_layer_close.svg';

const CLIENT_KEY = process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY;

function TossCheckout() {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state: any) => state.tossPay.userId);
  const amount = useAppSelector((state: any) => state.tossPay.amount);
  const requestPayments = useAppSelector(
    (state: any) => state.tossPay.requestPayments,
  );

  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [widgets, setWidgets] = useState<TossPaymentsWidgets | null>(null);
  const [paymentMethodWidget, setPaymentMethodWidget] =
    useState<WidgetPaymentMethodWidget | null>(null);

  const handleClose = () => {
    dispatch(setTossPay({ userId: '', amount: 0, requestPayments: null }));
  };

  useEffect(() => {
    if (userId && amount && requestPayments) {
      setOpen(true);
    } else {
      setOpen(false);
      setReady(false);
    }
  }, [userId, amount, requestPayments]);

  useEffect(() => {
    async function fetchPaymentWidgets() {
      const tossPayments = await loadTossPayments(CLIENT_KEY!);
      const widgets = tossPayments.widgets({
        customerKey: userId,
      });
      setWidgets(widgets);
    }

    if (open) {
      fetchPaymentWidgets();
    }
  }, [open]);

  useEffect(() => {
    async function renderPaymentWidgets() {
      if (widgets == null) {
        return;
      }
      // ------ 주문의 결제 금액 설정 ------
      await widgets.setAmount({ currency: 'KRW', value: amount });

      // ------  결제 UI 렌더링 ------
      const paymentMethodWidgetInstance = await widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'DEFAULT',
      });
      setPaymentMethodWidget(paymentMethodWidgetInstance);

      await widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT',
      });

      setReady(true);
    }

    renderPaymentWidgets().then(
      () => (document.body.style.pointerEvents = 'auto'),
    );

    return () => {
      if (paymentMethodWidget) {
        paymentMethodWidget.destroy().then(r => {
          setPaymentMethodWidget(null);
        });
      }
    };
  }, [widgets]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className={`w-[calc(100%-40px)] max-w-[600px] ${ready ? undefined : 'hidden'} max-h-[80%] overflow-y-auto`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="hidden">&nbsp;</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="wrapper">
          <div className="w-full inline-flex flex-row items-center justify-end">
            <Button size="icon" variant="ghost" onClick={handleClose}>
              <Image src={CloseIcon} alt="Close" width={20} height={20} />
            </Button>
          </div>
          <div className="box_section">
            {/* 결제 UI */}
            <div id="payment-method" />
            {/* 이용약관 UI */}
            <div id="agreement" />

            {/* 결제하기 버튼 */}
            <div className="px-[30px] py-6">
              <Button
                className="w-full bg-gradient-giftifan"
                disabled={!ready}
                onClick={async () => {
                  try {
                    if (!widgets) {
                      return;
                    }
                    // ------ '결제하기' 버튼 누르면 결제창 띄우기 ------
                    // 결제를 요청하기 전에 orderId, amount를 서버에 저장하세요.
                    // 결제 과정에서 악의적으로 결제 금액이 바뀌는 것을 확인하는 용도입니다.
                    await widgets.requestPayment(requestPayments);
                  } catch (error) {
                    // 에러 처리하기
                    console.error(error);
                  }
                }}
              >
                결제하기
              </Button>
            </div>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default TossCheckout;
